// VclURJDhIR5FyUXegLsy ,34.3015

import { useRef, useEffect, useState, useCallback } from "react";
import maplibregl from "maplibre-gl";
import { GeocodingControl } from "@maptiler/geocoding-control/react";
import { createMapLibreGlMapController } from "@maptiler/geocoding-control/maplibregl-controller";
import "@maptiler/geocoding-control/style.css";
import "@fortawesome/fontawesome-free/css/all.min.css";

import BasemapsControl from "maplibre-gl-basemaps";

import isEqual from 'lodash/isEqual';
import "maplibre-gl-basemaps/lib/basemaps.css";
import Draggable from "react-draggable";
import { basemaps } from "./UtilitiesCommon";
import {
  MaplibreExportControl,
  Size,
  PageOrientation,
  Format,
  DPI,
} from "@watergis/maplibre-gl-export";

import "./styles/MapStyle.css";
import InfoAnalysis from "./InfoAnalysis";
import {add_only_if_not_exists, remove_image_layer} from "./MapUtils";


export default function Map({
  precipitation_check,
  current_vector,
  current_spi,
  current_fapar_anomaly,
  fapar_anomaly_check,
  current_fapar_absorbed,
  fapar_absorbed_check,
  monthly_precipitation_check,
  current_monthly_precipitation_gpcc,
  current_sma,
  sma_check,
  cdi_check,
  current_cdi,
  cdi_selected_dates,
  current_ndvi,
  ndvi_check,
  koppen_climate_clas_check,
  koppen_climate_url,
  active_admin_boundaries,
  current_protected_areas,
  protetect_areas_check,
  clicked_latlong, 
  setClicked_latlong,
  pixelDataFetched, 
  setPixelDataFetched,
  setActive_tab,
  admin_details,
  show_generate_report,
  setShow_generate_report,
  current_unwdc,
  unwdc_check,
  checked_spi_era5,
  current_spi_era5,
  cdi_era5_check,
  current_cdi_era5,
  longrange_forecast_check,
  current_longrange_forecast,
  drought_persistence_check,
  current_drought_persistence,
  combined_hazard_era5_check,
  current_combined_hazard_era5,
  combined_hazard_gpcc_check,
  current_combined_hazard_gpcc,
  combined_forecast_era5_check,
  current_combined_forecast_era5,
  combined_forecast_gpcc_check,
  current_combined_forecast_gpcc,
  layerOpacity,

}) {
  // Map from MapLibre layer ID → opacity key used in layerOpacity state
  const LAYER_OPACITY_KEY = {
    cdi: "cdi", cdi_era5: "cdi",
    spi: "spi", spi_era5: "spi_era5",
    faparAnom: "faparAnom", faparAbsorbed: "faparAbsorbed",
    sma: "sma", ndvi: "ndvi", unwdc: "unwdc",
    monthly_precipitation_gpcc: "monthly_precipitation_gpcc",
    drought_persistence: "drought_persistence",
    longrange_forecast: "longrange_forecast",
    koppen_climate: "koppen_climate",
  };
  const mapContainer = useRef(null);
  const map = useRef(null);
  const exportControlAdded = useRef(false);
  const [lng] = useState(12.7322); // Easting
  const [lat] = useState(0.4542); // Nothing
  const [zoom] = useState(2.5);
  const [API_KEY] = useState("VclURJDhIR5FyUXegLsy");
  const [Mapbox_Key] = useState(
    "pk.eyJ1Ijoia29yeWtvcmlyMTIzIiwiYSI6ImNsMGdqcjdybTEzcTczanBybHU5anN6bnUifQ.rIUBT4fmSIwMuwN_vtUznw"
  );
  const [currentZoom, setCurrentZoom] = useState(zoom);

 


  const [mapController, setMapController] = useState();
  const previous_vector = useRef();
  const previous_protected_areas = useRef();
  const previous_spi = useRef();
  const previous_cdi = useRef();
  const previous_fapar_anomaly = useRef();
  const previous_fapar_absorbed = useRef();
  const previous_monthly_precipitation_gpcc = useRef();
  const previous_sma = useRef();
  const previous_ndvi = useRef();
  const previous_unwdc = useRef();
  const previous_koppen_climate = useRef();
  const previous_spi_era5 = useRef();
  const previous_cdi_era5 = useRef();
  const previous_longrange_forecast = useRef();
  const previous_drought_persistence = useRef();
  const previous_combined_hazard_era5 = useRef();
  const previous_combined_hazard_gpcc = useRef();
  const previous_combined_forecast_era5 = useRef();
  const previous_combined_forecast_gpcc = useRef();

  useEffect(() => {
    if (map.current) return;
    map.current = new maplibregl.Map({
      container: mapContainer.current,
    
      style: { version: 8, sources: {}, layers: [] },
      center: [lng, lat],
      zoom: zoom,
    });

    map.current.on("moveend", () => {
      setCurrentZoom(map.current.getZoom());
    });

    setMapController(createMapLibreGlMapController(map.current, maplibregl));

    map.current.addControl(
      new BasemapsControl({
        basemaps: basemaps,
        initialBasemap: "mapbox_streets",
        expandDirection: "top",
      }),
      "bottom-left"
    );
    map.current.addControl(new maplibregl.NavigationControl(), "bottom-left");
  });

  useEffect(() => {
    if (map.current && !exportControlAdded.current) {
      // Only add the control if it hasn't been added before
      const exportControl = new MaplibreExportControl({
        PageSize: Size.A4,
        PageOrientation: PageOrientation.Landscape,
        Format: Format.PNG,
        DPI: DPI[300],
        Local: "en",
        PrintableArea: true,
        Crosshair: true,
      });
      // map.current.addControl(exportControl, "top-left");
      exportControlAdded.current = true; // Mark the control as added
    }
  }, [map.current]);

  const add_vector = (url, name, bounds, type = "raster") => {
    if (url !== null && map.current) {
      const [minLng, minLat, maxLng, maxLat] = [
        bounds["minx"],
        bounds["miny"],
        bounds["maxx"],
        bounds["maxy"],
      ];

      // Fit the map to the bounds regardless of the active_admin_boundaries status
      map.current.fitBounds([
        [minLng, minLat],
        [maxLng, maxLat],
      ]);

      // Check if a source with the same name already exists
      const sourceExists = map.current.getSource(name);

      if (active_admin_boundaries) {
        if (sourceExists) {
          // If the layer exists, remove it before adding a new one
          map.current.removeLayer(name);
          map.current.removeSource(name);
        }

        // Add new source and layer
        map.current.addSource(name, {
          type: type,
          tiles: [url],
        });

        const layer = {
          id: name,
          type: type,
          source: name,
          tileSize: 256,
        };
        map.current.addLayer(layer);
      } else if (sourceExists) {
        // If active_admin_boundaries is false and the layer exists, remove it
        map.current.removeLayer(name);
        map.current.removeSource(name);
      }
    }
  };

  const add_image_geoserver = useCallback((url, name, state_check = false) => {
    if (state_check == false) {
      if (map.current && map.current.getSource(name)) {
        map.current.removeLayer(name);
        map.current.removeSource(name);
      }
    }

    const opacityKey = LAYER_OPACITY_KEY[name];
    const layer = {
      id: name,
      type: "raster",
      source: name,
      crs: "EPSG:3857",
      tileSize: 256,
      params: {
        LAYERS: name,
        CRS: "EPSG:3857",
        TILED: true,
      },
      paint: {
        "raster-opacity": layerOpacity?.[opacityKey] ?? 1,
      },
    };

    if (map.current && map.current.getSource(name)) {
      map.current.removeLayer(name);
      map.current.removeSource(name);
      if (state_check) {
        map.current.addSource(name, {
          type: "raster",
          tiles: [url],
        });
        map.current.addLayer(layer);
      } else {
        map.current.removeLayer(name);
        map.current.removeSource(name);
      }
    } else {
      map.current.addSource(name, {
        type: "raster",
        tiles: [url],
        crs: "ESPG:4326",
      });
      map.current.addLayer(layer);
      if (state_check) {
        map.current.addSource(name, {
          type: "raster",
          tiles: [url],
        });
        map.current.addLayer(layer);
      } else {
        map.current.removeLayer(name);
        map.current.removeSource(name);
      }
    }
  }, []);
  const dekad_obj = { "1st": "01", "2end": "11", "3rd": "21" };

  // Apply opacity changes to already-rendered layers without re-adding them
  useEffect(() => {
    if (!map.current || !layerOpacity) return;
    const safeSet = (mapId, opacityKey) => {
      if (map.current.getLayer(mapId)) {
        map.current.setPaintProperty(mapId, "raster-opacity", layerOpacity[opacityKey] ?? 1);
      }
    };
    safeSet("cdi",                        "cdi");
    safeSet("cdi_era5",                   "cdi");
    safeSet("spi",                        "spi");
    safeSet("spi_era5",                   "spi_era5");
    safeSet("faparAnom",                  "faparAnom");
    safeSet("faparAbsorbed",              "faparAbsorbed");
    safeSet("monthly_precipitation_gpcc", "monthly_precipitation_gpcc");
    safeSet("sma",                        "sma");
    safeSet("ndvi",                       "ndvi");
    safeSet("unwdc",                      "unwdc");
    safeSet("drought_persistence",        "drought_persistence");
    safeSet("longrange_forecast",         "longrange_forecast");
    safeSet("koppen_climate",             "koppen_climate");
  }, [layerOpacity]);

 

  useEffect(() => {
    if (map.current) {
      map.current.on("click", (e) => {
        setPixelDataFetched(false);
        var lon_lat = e.lngLat;

        if (!pixelDataFetched) {
          setClicked_latlong({ lat: lon_lat["lat"], lon: lon_lat["lng"] });

        }
        setPixelDataFetched(true);
        setActive_tab("Analysis");
      });
    }
  }, [cdi_selected_dates]);

  var admn_1 =
    "https://ada.acmad.org/geoserver/wms?bbox={bbox-epsg-3857}&format=image/png&service=WMS&version=1.1.1&request=GetMap&srs=EPSG:3857&width=256&height=256&transparent=true&layers=africa_drought_datasets:data_api_adminlevelone";

  var style = {
    nodata: -99999,
    min: 1,
    max: 3,
    clamp: false,
    dtype: "uint8",
    // scheme: "discrete",
    palette: ["#66b3ff", "#ffffff", "#FF6666"],
  };
  

  useEffect(() => {
    setTimeout(function () {
      if (currentZoom > 5.2) {
        add_image_geoserver(admn_1, "admn_1", false);
      } else {
        add_image_geoserver(admn_1, "admn_1", false);
      }
    }, 10);
  }, [currentZoom]);

  useEffect(() => {
    const get_wms = async () => {
      setTimeout(function () {
  

    const handle_layer_on_map = (previous, current, url, name, checked) =>{
    if (!isEqual(previous.current, current)) {
      add_image_geoserver(url, name, checked);
    }else {
      add_only_if_not_exists(map.current, url, name, checked);
      if (!checked) {
        remove_image_layer(map.current, name);
      }
     
    }
  }
        if (current_protected_areas !== null) {
        
          handle_layer_on_map(previous_protected_areas, current_protected_areas, current_protected_areas, "protected_areas", protetect_areas_check);

        }
        // spi
        if (current_spi !== null) {

          handle_layer_on_map(previous_spi, current_spi, current_spi, "spi", precipitation_check);
        }

        // cdi
        if (current_cdi !== null) {
          handle_layer_on_map(previous_cdi, current_cdi, current_cdi["cdi_geoserver"], "cdi", cdi_check);
        }

        // fapar_anormaly

        if (current_fapar_anomaly !== null) {

          handle_layer_on_map(previous_fapar_anomaly, current_fapar_anomaly, current_fapar_anomaly["fapar_anomaly_geoserver"], "faparAnom", fapar_anomaly_check);
          
        }

        // fapar_absorbed
        if (current_fapar_absorbed !== null) {
          // 
          handle_layer_on_map(previous_fapar_absorbed, current_fapar_absorbed, current_fapar_absorbed["fapar_absorbed_geoserver"], "faparAbsorbed", fapar_absorbed_check);
        }
        // monthly_precipitation_gpcc
        if (current_monthly_precipitation_gpcc !== null) {
          handle_layer_on_map(previous_monthly_precipitation_gpcc, current_monthly_precipitation_gpcc, current_monthly_precipitation_gpcc["monthly_precipitation_gpcc_geoserver"], "monthly_precipitation_gpcc", monthly_precipitation_check);
        }
        // sma

        if (current_sma !== null) {
          handle_layer_on_map(previous_sma, current_sma, current_sma["sma_geoserver"], "sma", sma_check);
        }
        // modis_ndvi

        if (current_ndvi !== null) {
          add_image_geoserver(current_ndvi, "ndvi", ndvi_check);
          handle_layer_on_map(previous_ndvi, current_ndvi, current_ndvi, "ndvi", ndvi_check);
        }

        // unwdc
        if (current_unwdc !== null) {
          handle_layer_on_map(previous_unwdc, current_unwdc, current_unwdc["unwdc_geoserver"], "unwdc", unwdc_check);
        }

        // spi era 5 
        if (current_spi_era5 !== null) {
          handle_layer_on_map(previous_spi_era5, current_spi_era5, current_spi_era5["spi_era5_geoserver"], "spi_era5", checked_spi_era5);
        }

        if (current_cdi_era5 !== null) {
          handle_layer_on_map(previous_cdi_era5, current_cdi_era5, current_cdi_era5["cdi_era5_geoserver"], "cdi_era5", cdi_era5_check);
        }

        // longrange_forecast
        if (current_longrange_forecast !== null) {
          handle_layer_on_map(previous_longrange_forecast, current_longrange_forecast, current_longrange_forecast["longrange_forecast_geoserver"], "longrange_forecast", longrange_forecast_check);
        }

        // drought_persistence
        if (current_drought_persistence !== null) {
          handle_layer_on_map(previous_drought_persistence, current_drought_persistence, current_drought_persistence, "drought_persistence", drought_persistence_check);
          console.log("current_drought_persistence", current_drought_persistence);
        }

        // combined monitoring & forecast hazard indicator
        if (current_combined_hazard_era5 !== null) {
          handle_layer_on_map(previous_combined_hazard_era5, current_combined_hazard_era5, current_combined_hazard_era5, "combined_hazard_era5", combined_hazard_era5_check);
        }
        if (current_combined_hazard_gpcc !== null) {
          handle_layer_on_map(previous_combined_hazard_gpcc, current_combined_hazard_gpcc, current_combined_hazard_gpcc, "combined_hazard_gpcc", combined_hazard_gpcc_check);
        }
        if (current_combined_forecast_era5 !== null) {
          handle_layer_on_map(previous_combined_forecast_era5, current_combined_forecast_era5, current_combined_forecast_era5, "combined_forecast_era5", combined_forecast_era5_check);
        }
        if (current_combined_forecast_gpcc !== null) {
          handle_layer_on_map(previous_combined_forecast_gpcc, current_combined_forecast_gpcc, current_combined_forecast_gpcc, "combined_forecast_gpcc", combined_forecast_gpcc_check);
        }

        // koppen_climate
        if (koppen_climate_url !== null) {
          add_image_geoserver(
            koppen_climate_url?.geoserver,
            "koppen_climate",
            koppen_climate_clas_check
          );
        }
        previous_protected_areas.current = current_protected_areas;
        previous_vector.current = current_vector;
        previous_spi.current = current_spi;
        previous_cdi.current = current_cdi;
        previous_fapar_anomaly.current = current_fapar_anomaly;
        previous_fapar_absorbed.current = current_fapar_absorbed;
        previous_monthly_precipitation_gpcc.current = current_monthly_precipitation_gpcc;
        previous_sma.current = current_sma;
        previous_ndvi.current = current_ndvi;
        previous_unwdc.current = current_unwdc;
        previous_koppen_climate.current = koppen_climate_url;
        previous_spi_era5.current = current_spi_era5;
        previous_cdi_era5.current = current_cdi_era5;
        previous_longrange_forecast.current = current_longrange_forecast;
        previous_drought_persistence.current = current_drought_persistence;
        previous_combined_hazard_era5.current = current_combined_hazard_era5;
        previous_combined_hazard_gpcc.current = current_combined_hazard_gpcc;
        previous_combined_forecast_era5.current = current_combined_forecast_era5;
        previous_combined_forecast_gpcc.current = current_combined_forecast_gpcc;

      }, 0);

    
    };

  

    get_wms().then(() => {
      setTimeout(function () {
        if (current_vector !== null) {
          var vector_url = current_vector["url"];
          var bounds = current_vector["bounds"];
      
            if(!isEqual(previous_vector.current, current_vector) ){
          add_vector(vector_url, "current_vector", bounds);

        }else {
          add_image_geoserver(vector_url, "current_vector", active_admin_boundaries);
         
        }
         
        }
      }, 100);
    });

    
  }, [
    current_spi,
    precipitation_check,
    current_vector,
    current_fapar_anomaly,
    fapar_anomaly_check,
    fapar_absorbed_check,
    current_fapar_absorbed,
    monthly_precipitation_check,
    current_monthly_precipitation_gpcc,
    current_sma,
    sma_check,
    cdi_check,
    current_cdi,
    current_ndvi,
    ndvi_check,
    koppen_climate_clas_check,
    protetect_areas_check,
    current_protected_areas,
    unwdc_check,
    current_unwdc,
    checked_spi_era5,
    current_spi_era5,
    cdi_era5_check,
    current_cdi_era5,
    longrange_forecast_check,
    current_longrange_forecast,
    drought_persistence_check,
    current_drought_persistence,
    combined_hazard_era5_check,
    current_combined_hazard_era5,
    combined_hazard_gpcc_check,
    current_combined_hazard_gpcc,
    combined_forecast_era5_check,
    current_combined_forecast_era5,
    combined_forecast_gpcc_check,
    current_combined_forecast_gpcc
  ]);



  return (
    <div className="map-wrap">
      <div className="geocoding">
        <GeocodingControl apiKey={API_KEY} mapController={mapController} />
      </div>
      <div ref={mapContainer} className="map" />
      {show_generate_report && (
        <Draggable>
          <div className="analysis-info" 
          // id="draggableChart"
          >
            <InfoAnalysis
              show_generate_report={show_generate_report}
              admin_details={admin_details}
              setShow_generate_report={setShow_generate_report}
            />
          </div>
        </Draggable>
      )}
    </div>
  );
}
